import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { categoryService } from '@/services/category.service';
import { productService } from '@/services/product.service';
import { Product } from '@/types/product.types';
import { CategoryWithSubCategories, SubCategory } from '@/types/category.types';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/utils/formatters';
import '@/styles/category-products.css';
import { useTranslation } from 'react-i18next';

const toCategorySlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function resolveCategoryOrSub(
  slug: string,
  categories: CategoryWithSubCategories[]
): { kind: 'parent'; category: CategoryWithSubCategories } | { kind: 'sub'; category: CategoryWithSubCategories; sub: SubCategory } | null {
  const active = categories.filter((c) => c.is_active);
  for (const cat of active) {
    if (cat.slug === slug || toCategorySlug(cat.name) === slug) {
      return { kind: 'parent', category: cat };
    }
  }
  for (const cat of active) {
    for (const sub of cat.sub_categories || []) {
      if (!sub.is_active) continue;
      if (sub.slug === slug || toCategorySlug(sub.name) === slug) {
        return { kind: 'sub', category: cat, sub };
      }
    }
  }
  return null;
}

function productBelongsToParent(product: Product, category: CategoryWithSubCategories): boolean {
  const pc = (product.category || '').trim().toLowerCase();
  if (!pc) return false;
  if (pc === category.name.trim().toLowerCase()) return true;
  for (const sub of category.sub_categories || []) {
    if (!sub.is_active) continue;
    if (pc === sub.name.trim().toLowerCase()) return true;
  }
  return false;
}

export const CategoryProducts = () => {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [titleStrong, setTitleStrong] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategoryProducts = async () => {
      try {
        setLoading(true);
        const [categories, allProducts] = await Promise.all([
          categoryService.getAll(),
          productService.getAll(),
        ]);

        const resolved = resolveCategoryOrSub(slug, categories);

        if (!resolved) {
          setTitleStrong(t('categoryPage.notFound'));
          setProducts([]);
          return;
        }

        const published = allProducts.filter(
          (p) => p.status === 'published' && p.stock_status === 'in-stock'
        );

        if (resolved.kind === 'sub') {
          setTitleStrong(resolved.sub.name);
          const subLower = resolved.sub.name.trim().toLowerCase();
          setProducts(
            published.filter((p) => (p.category || '').trim().toLowerCase() === subLower)
          );
        } else {
          setTitleStrong(resolved.category.name);
          setProducts(published.filter((p) => productBelongsToParent(p, resolved.category)));
        }
      } catch (error) {
        console.error('Erreur chargement produits categorie:', error);
        setTitleStrong(t('categoryPage.notFound'));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategoryProducts();
  }, [slug, t, i18n.language]);

  const normalizedProducts = useMemo(() => products, [products]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="category-products-page">
        <div className="category-products-container">
          <h1 className="gtitle category-products-title">
            <span className="gtitle-light">{t('categoryPage.titleLight')}</span>
            <span className="gtitle-strong">{loading ? '\u2026' : titleStrong}</span>
          </h1>

          {loading ? (
            <p className="category-products-state">{t('categoryPage.loading')}</p>
          ) : normalizedProducts.length === 0 ? (
            <p className="category-products-state">{t('categoryPage.empty')}</p>
          ) : (
            <div className="category-products-grid">
              {normalizedProducts.map((product) => {
                const finalPrice = product.reduced_price || product.price;
                const hasDiscount = product.reduced_price && product.reduced_price < product.price;
                return (
                  <article
                    key={product.id}
                    className="category-product-card"
                    onClick={() => navigate(`/produit-detail?id=${product.id}`)}
                  >
                    <div className="category-product-image-wrap">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="category-product-image" />
                      ) : (
                        <div className="category-product-image-empty">Image indisponible</div>
                      )}
                    </div>
                    <div className="category-product-content">
                      <h3>{product.name}</h3>
                      <p className="category-product-price">{formatPrice(finalPrice)}</p>
                      {hasDiscount && (
                        <p className="category-product-old-price">{formatPrice(product.price)}</p>
                      )}
                      <button
                        type="button"
                        className="category-product-cart-btn"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await addToCart(product.id, 1);
                        }}
                      >
                        {t('categoryPage.addToCart')}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
